import { Chip, Box, Typography } from "@wealthx/ui";
import React, { useEffect, useRef } from "react";

import classNames from "classnames";

import { BrowserPlan, ActionStatus } from "../../api-services/ai-chat";
import BrowserPlansList from "../BrowserPlansList";
import MarkdownText from "../MarkdownText";
import StepProgressIndicator from "../StepProgressIndicator";
import { PolicySource, LookupStep } from "../../type/chat";
import { BROWSER_PLANS_TAG_OPEN, BROWSER_PLANS_TAG_CLOSE } from "../../commons/ai-chat.constants";

interface ChatMessageListProps {
  messages?: {
    id: string;
    role: "user" | "assistant";
    content: string;
    files?: { url?: string; name?: string; type?: string }[];
    isStreaming?: boolean;
    isErrored?: boolean;
    browserPlans?: BrowserPlan[];
    policySources?: PolicySource[];
  }[];
  currentStep?: LookupStep | null;
  onPlanClick?: (plan: BrowserPlan, messageIndex: number) => void;
  actionStatuses?: Record<number, Record<string, Record<string, ActionStatus>>>;
  executingPlans?: Set<string>;
}

const ChatMessageList = ({
  messages = [],
  currentStep,
  onPlanClick,
  actionStatuses = {},
  executingPlans = new Set(),
}: ChatMessageListProps) => {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const browserPlansTagRegex = new RegExp(
    `${BROWSER_PLANS_TAG_OPEN}[\\s\\S]*?${BROWSER_PLANS_TAG_CLOSE}`,
    "gi"
  );

  useEffect(() => {
    if (!bottomRef.current) return;
    bottomRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages?.length]);

  return (
    <Box className="chats-page__messages">
      {messages.map((msg, index) => {
        const isUser = msg.role === "user";
        const hasFiles = Array.isArray(msg.files) && msg.files.length > 0;
        // Remove <matched_plans> tags from content before rendering
        const sanitizedContent = msg.content.replace(browserPlansTagRegex, "").trim();

        const isLastAssistantWithStep =
          index === messages.length - 1 && msg.role === "assistant" && currentStep;

        let stepOrTypingContent: React.ReactNode = null;
        if (isLastAssistantWithStep) {
          stepOrTypingContent = <StepProgressIndicator step={currentStep} />;
        } else if (!sanitizedContent) {
          stepOrTypingContent = (
            <span className="chats-page__typing">
              <span className="chats-page__typing-dot" />
              <span className="chats-page__typing-dot chats-page__typing-dot--delay1" />
              <span className="chats-page__typing-dot chats-page__typing-dot--delay2" />
            </span>
          );
        }

        return (
          <div
            key={msg.id}
            className={classNames("chats-page__message-row", {
              "chats-page__message-row--user": isUser,
              "chats-page__message-row--assistant": !isUser,
            })}
          >
            <div
              className={classNames("chats-page__message-bubble", {
                "chats-page__message-bubble--user": isUser,
                "chats-page__message-bubble--assistant": !isUser,
                "chats-page__message-bubble--error": msg.isErrored,
              })}
            >
              {(sanitizedContent ||
                msg.isStreaming ||
                (index === messages.length - 1 && msg.role === "assistant" && currentStep)) && (
                <Typography component="div" className="chats-page__message-text">
                  {sanitizedContent ? (
                    <MarkdownText text={sanitizedContent} policySources={msg.policySources} />
                  ) : null}
                  {stepOrTypingContent}
                </Typography>
              )}

              {msg.browserPlans && msg.browserPlans.length > 0 && (
                <BrowserPlansList
                  plans={msg.browserPlans}
                  onPlanClick={onPlanClick}
                  actionStatuses={actionStatuses}
                  executingPlans={executingPlans}
                  messageIndex={index}
                />
              )}

              {hasFiles && (
                <div className="chats-page__message-files">
                  {msg.files?.map((file) => {
                    const isImage = file?.type?.startsWith("image");
                    if (isImage && file.url) {
                      return (
                        <div key={file.url} className="chats-page__message-image-wrapper">
                          <img
                            src={file.url}
                            alt={file.name || "attachment"}
                            className="chats-page__message-image"
                          />
                        </div>
                      );
                    }
                    return (
                      <Chip
                        key={file.url || file.name}
                        label={file.name || "Attachment"}
                        component="a"
                        clickable
                        href={file.url || "#"}
                        target={file.url ? "_blank" : undefined}
                        rel="noreferrer"
                        className="chats-page__message-file"
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </Box>
  );
};

export default ChatMessageList;
